import { api } from '@/helpers/apiClient'
import logger, { toErrorMessage } from '@/helpers/logger'
import { z } from 'zod'
import {
  treeVideoDtoSchema,
  treeVideoListDtoSchema,
  treeVideoUploadTargetDtoSchema,
  type TreeVideoDto,
  type TreeVideoListDto,
  type TreeVideoUploadTargetDto,
} from '@lib/dto/tree-video.dto'
import type {
  CreateTreeVideoInput,
  VideoOwner,
} from '@lib/input/tree-video.input'
import type { ITreeVideoService } from '@/types/TreeVideoService.types'
import {
  uploadWithProgress,
  type UploadProgress,
} from '@/helpers/uploadWithProgress'
import { withUploadRetry } from '@/helpers/retryUpload'
import { toQueryString } from '@/helpers/queryString'

/**
 * A video may take a while on a bad connection, so the timeout is generous —
 * but it is finite, which is the point. `fetch` has no timeout at all, and a
 * stalled upload with no deadline is indistinguishable from a working one.
 */
const VIDEO_UPLOAD_TIMEOUT_MS = 10 * 60 * 1000

const availabilitySchema = z.object({ enabled: z.boolean() })

/**
 * Videos of a tree or an irrigation record, and stock clips.
 *
 * A tree's are under `/trees/:treeId/videos`; an irrigation record's under
 * `/irrigation-videos`, which names the record in the query or body because
 * one route serves four kinds of record.
 *
 * `upload` runs the handshake and is the second place in the web app that calls
 * `fetch` outside the `api` client — the middle step POSTs to Cloudflare
 * Stream, and the `api` client attaches our bearer token to every request.
 * Sending a session token to a third-party host would leak it. See
 * `DocumentService` for the same reasoning about R2.
 */
class TreeVideoService implements ITreeVideoService {
  async availability(): Promise<{ enabled: boolean }> {
    try {
      return await api.get('/tree-videos/availability', availabilitySchema)
    } catch (error) {
      logger.error('Failed to check video availability', {
        error: toErrorMessage(error),
      })
      throw error
    }
  }

  async list(
    owner: VideoOwner,
    visitId: string | null = null,
  ): Promise<TreeVideoListDto> {
    try {
      return await api.get(
        'treeId' in owner
          ? `/trees/${encodeURIComponent(owner.treeId)}/videos${toQueryString({ visitId })}`
          : `/irrigation-videos${toQueryString(owner)}`,
        treeVideoListDtoSchema,
      )
    } catch (error) {
      logger.error('Failed to list videos', {
        ...owner,
        error: toErrorMessage(error),
      })
      throw error
    }
  }

  /**
   * Tell the API a reservation was never used, so the placeholder goes away.
   *
   * Best-effort by design: this runs on a failure path, very likely on a
   * connection that is already in trouble. If it cannot get through, the
   * server writes the reservation off on its own once the upload URL has
   * expired.
   */
  private async abandon(videoId: string): Promise<void> {
    try {
      // 204 with an empty body. `api.post` parses that body, and an empty
      // body is `null`, which is not a successful response — so a reservation
      // the server had already released was logged as a failure.
      await api.postNoContent(
        `/tree-videos/${encodeURIComponent(videoId)}/abandon`,
        {},
      )
    } catch (error) {
      logger.warn('Could not abandon an unfinished video', {
        videoId,
        error: toErrorMessage(error),
      })
    }
  }

  /** Ask the API for a one-time Stream upload URL for this owner. */
  private async reserve(
    owner: VideoOwner | null,
    input: CreateTreeVideoInput,
  ): Promise<TreeVideoUploadTargetDto> {
    if (owner === null) {
      return api.post(
        '/tree-videos/upload-url',
        input,
        treeVideoUploadTargetDtoSchema,
      )
    }
    if ('treeId' in owner) {
      return api.post(
        `/trees/${encodeURIComponent(owner.treeId)}/videos/upload-url`,
        input,
        treeVideoUploadTargetDtoSchema,
      )
    }
    return api.post(
      '/irrigation-videos/upload-url',
      { ...input, ...owner },
      treeVideoUploadTargetDtoSchema,
    )
  }

  /**
   * Reserve, send, and hand back the row.
   *
   * The row comes back PENDING_UPLOAD or PROCESSING — deliberately not READY.
   * Stream transcodes afterwards, so there is nothing to confirm at this point
   * the way a document's upload can be confirmed; the list catches it up.
   *
   * **Each retry re-reserves.** A Stream direct-upload URL is one-shot, so
   * pointing a second POST at a URL a failed one already touched is not
   * something to rely on. Asking for a fresh reservation costs one small
   * request and makes the retry unambiguous — and the abandoned one is cleaned
   * up rather than left as a placeholder Cloudflare charges for.
   */
  async upload(
    /** Null reserves a stock clip for the problem directory, owned by nothing. */
    owner: VideoOwner | null,
    file: File,
    input: CreateTreeVideoInput,
    options: {
      signal?: AbortSignal
      onProgress?: (progress: UploadProgress) => void
    } = {},
  ): Promise<TreeVideoDto> {
    let uploaded: TreeVideoDto | null = null

    await withUploadRetry(
      async () => {
        const target = await this.reserve(owner, input)

        // Stream's direct-upload endpoint takes multipart, not a raw body.
        const form = new FormData()
        form.append('file', file)

        try {
          // XHR, not fetch: see `uploadWithProgress`. Our bearer token is
          // never attached, so it cannot reach the storage host.
          await uploadWithProgress({
            url: target.uploadUrl,
            method: 'POST',
            body: form,
            timeoutMs: VIDEO_UPLOAD_TIMEOUT_MS,
            ...(options.signal ? { signal: options.signal } : {}),
            ...(options.onProgress ? { onProgress: options.onProgress } : {}),
          })
        } catch (error) {
          await this.abandon(target.video.id)
          throw error
        }

        uploaded = target.video
      },
      { label: `video:${owner === null ? 'library' : Object.values(owner).join('')}` },
    )

    if (uploaded === null) {
      throw new Error('The video could not be uploaded.')
    }
    return uploaded
  }

  /**
   * One video by id.
   *
   * How a stock clip is read: it belongs to no tree, so there is no list to
   * find it in. The API refreshes it from Stream on the way out, which is what
   * lets a clip stop saying "Processing" without a webhook.
   */
  async get(id: string): Promise<TreeVideoDto> {
    try {
      return await api.get(
        `/tree-videos/${encodeURIComponent(id)}`,
        treeVideoDtoSchema,
      )
    } catch (error) {
      logger.error('Failed to load a video', { id, error: toErrorMessage(error) })
      throw error
    }
  }

  async archive(id: string): Promise<TreeVideoDto> {
    try {
      return await api.post(
        `/tree-videos/${encodeURIComponent(id)}/archive`,
        {},
        treeVideoDtoSchema,
      )
    } catch (error) {
      logger.error('Failed to archive tree video', {
        id,
        error: toErrorMessage(error),
      })
      throw error
    }
  }
}

export const treeVideoService = new TreeVideoService()
